import { ReactNode } from 'react';
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormLabel,
  forwardRef,
  SwitchProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import Box from '../Box/Box';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';
import Text from '../Text/Text';
import Skeleton from '../Skeleton/Skeleton';
import SkeletonBox from '../Skeleton/SkeletonBox';
import ToggleComponent from './ToggleComponent';

type VariantLabel =
  | { variant?: undefined; label?: ReactNode }
  | { variant: 'dynamic'; label?: ReactNode }
  | { variant: 'fixed'; label: ReactNode };
export type ToggleProps = Omit<FormControlProps, 'label' | 'onBlur' | 'onChange'> & {
  'data-testid'?: string;
  defaultChecked?: SwitchProps['defaultChecked'];
  id?: SwitchProps['id'];
  isChecked?: SwitchProps['isChecked'];
  isDisabled?: SwitchProps['isDisabled'];
  isLoading?: boolean;
  helperText?: ReactNode;
  name?: string;
  onBlur?: SwitchProps['onBlur'];
  onChange?: SwitchProps['onChange'];
  tooltip?: Omit<TooltipProps, 'children'>;
  value?: SwitchProps['value'];
  valueText?: ReactNode;
} & VariantLabel;

/**
 * The Toggle component is used as an alternative for the checkbox component.
 */
const Toggle = forwardRef<ToggleProps, 'div'>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    'data-testid': dataTestid,
    defaultChecked,
    helperText,
    id,
    isChecked,
    isDisabled,
    isInvalid,
    isReadOnly,
    isLoading,
    label,
    name,
    onBlur,
    onChange,
    tooltip,
    value,
    valueText,
    variant = 'dynamic',
    ...rest
  } = props;
  const size: 'sm' | 'md' = variant === 'dynamic' ? 'md' : 'sm';
  const css = useMultiStyleConfig('Switch', { withTooltip: !!tooltip, size, variant });
  const formControlProps = {
    sx: css.form,
    isDisabled,
    isInvalid,
    ...rest,
    ref,
  };
  const switchProps = {
    'aria-label': ariaLabel,
    'data-testid': dataTestid,
    defaultChecked,
    id,
    isChecked,
    isDisabled,
    isReadOnly,
    isLoading,
    name,
    onBlur,
    onChange,
    value,
    size,
  };
  const helperBlock = () => {
    if (!helperText) return;
    if (isLoading) {
      return (
        <Skeleton marginTop="4" maxWidth="120px" isActive paddingY="0.125rem">
          <SkeletonBox height="12" />
        </Skeleton>
      );
    }
    return (
      <FormHelperText sx={css.helperText} as="p">
        {helperText}
      </FormHelperText>
    );
  };
  const switchComponent = () => {
    if (tooltip)
      return (
        <Tooltip {...tooltip}>
          <ToggleComponent {...switchProps} />
        </Tooltip>
      );
    return <ToggleComponent {...switchProps} />;
  };
  return (
    <FormControl {...formControlProps}>
      {variant === 'fixed' && (
        <>
          <Box>
            <FormLabel htmlFor={id} sx={css.label}>
              {label}
            </FormLabel>
            {helperBlock()}
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap="8">
            {switchComponent()}
          </Box>
        </>
      )}
      {variant === 'dynamic' && (
        <>
          {label && (
            <FormLabel htmlFor={id} sx={css.label}>
              {label}
            </FormLabel>
          )}
          <Box display="flex" flexDirection="row" alignItems="center" gap="8">
            {switchComponent()}
            {isLoading ? (
              <Skeleton maxWidth="7.5rem" flex="1" isActive>
                <SkeletonBox height="16" />
              </Skeleton>
            ) : (
              <Text sx={css.valueText}>{valueText}</Text>
            )}
          </Box>
          {helperBlock()}
        </>
      )}
    </FormControl>
  );
});

export default Toggle;
